import type { ReactNode } from "react";
import Link from "next/link";
import { ChevronDown } from "@/components/icons";
import { cn } from "@/lib/utils";
import { FaqAskRow } from "./faq-ask-row";
import {
  FAQ_ROW_STYLES,
  FAQ_SUMMARY_BASE,
  type FaqRowVariant,
} from "./faq-row-styles";

export interface FaqRowItem {
  question: ReactNode;
  answer: ReactNode;
  links?: Array<{ label: string; href: string }>;
}

interface FaqRowsProps {
  items: FaqRowItem[];
  variant?: FaqRowVariant;
  isAskable?: boolean;
  className?: string;
}

function paragraphs(answer: string): string[] {
  return answer
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function FaqAnswer({ answer }: { answer: ReactNode }) {
  if (typeof answer !== "string") return <>{answer}</>;
  return (
    <>
      {paragraphs(answer).map((part, partIndex) => (
        <p key={partIndex}>{part}</p>
      ))}
    </>
  );
}

export function FaqRows({
  items,
  variant = "plain",
  isAskable = true,
  className,
}: FaqRowsProps) {
  const styles = FAQ_ROW_STYLES[variant];

  return (
    <div className={cn("ph-no-capture divide-y divide-border", className)}>
      {items.map((item, itemIndex) => (
        <details key={itemIndex} className="group">
          <summary className={cn(FAQ_SUMMARY_BASE, styles.summary)}>
            <span>{item.question}</span>
            <ChevronDown
              aria-hidden
              className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
            />
          </summary>
          <div className={styles.body}>
            <FaqAnswer answer={item.answer} />
            {item.links && item.links.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-3">
                {item.links.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="text-link hover:text-link-hover hover:underline"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        </details>
      ))}
      {isAskable ? <FaqAskRow variant={variant} /> : null}
    </div>
  );
}
